import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { CanvasApi, extractPageData, extractPageListData } from './canvasApi';
import {
  Page,
  CreatePageRequest,
  UpdatePageRequest,
} from '../../types/canvas.types';

// Query keys
export const pageKeys = {
  all: ['pages'] as const,
  lists: () => [...pageKeys.all, 'list'] as const,
  list: (accountId: string, canvasId: string) =>
    [...pageKeys.lists(), accountId, canvasId] as const,
  details: () => [...pageKeys.all, 'detail'] as const,
  detail: (accountId: string, pageId: string) =>
    [...pageKeys.details(), accountId, pageId] as const,
  bySlug: (accountId: string, canvasId: string, pageSlug: string) =>
    [...pageKeys.details(), accountId, canvasId, 'slug', pageSlug] as const,
};

// Hook to fetch all pages for a canvas
export function usePages(accountId: string, canvasId: string) {
  return useQuery({
    queryKey: pageKeys.list(accountId, canvasId),
    queryFn: async () => {
      const response = await CanvasApi.listPages(accountId, canvasId).run();
      return extractPageListData(response);
    },
    enabled: !!accountId && !!canvasId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to fetch a single page
export function usePage(pageId: string, accountId?: string) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];

  return useQuery({
    queryKey: pageKeys.detail(finalAccountId!, pageId),
    queryFn: async () => {
      const response = await CanvasApi.getPage(finalAccountId!, pageId).run();
      return extractPageData(response);
    },
    enabled: !!finalAccountId && !!pageId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to fetch a page by slug
export function usePageBySlug(
  accountId: string,
  canvasId: string,
  pageSlug: string,
) {
  return useQuery({
    queryKey: pageKeys.bySlug(accountId, canvasId, pageSlug),
    queryFn: async () => {
      // Fetch all pages and find the one with matching slug
      const response = await CanvasApi.listPages(accountId, canvasId).run();
      const pages = extractPageListData(response);
      const page = pages.find((p) => p.slug === pageSlug);

      if (!page) {
        throw new Error(`Page with slug "${pageSlug}" not found`);
      }

      return page;
    },
    enabled: !!accountId && !!canvasId && !!pageSlug,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to create a new page
export function useCreatePage(accountId: string, canvasId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePageRequest) => {
      const response = await CanvasApi.createPage(
        accountId,
        canvasId,
        data,
      ).run();
      return extractPageData(response);
    },
    onSuccess: (newPage) => {
      // Invalidate and refetch pages list
      queryClient.invalidateQueries({
        queryKey: pageKeys.list(accountId, canvasId),
      });

      // Optimistically update the cache
      queryClient.setQueryData(pageKeys.detail(accountId, newPage.id), newPage);
    },
  });
}

// Hook to update a page
export function useUpdatePage(
  pageId: string,
  canvasId: string,
  accountId?: string,
) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdatePageRequest) => {
      const response = await CanvasApi.updatePage(
        finalAccountId!,
        pageId,
        data,
      ).run();
      return extractPageData(response);
    },
    onSuccess: (updatedPage) => {
      // Update the specific page in cache
      queryClient.setQueryData(
        pageKeys.detail(finalAccountId!, pageId),
        updatedPage,
      );

      // Update the page in the list
      queryClient.setQueryData(
        pageKeys.list(finalAccountId!, canvasId),
        (oldData: Page[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((page) =>
            page.id === pageId ? updatedPage : page,
          );
        },
      );
    },
  });
}

// Hook to delete a page
export function useDeletePage(canvasId: string, accountId?: string) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pageId: string) => {
      await CanvasApi.deletePage(finalAccountId!, pageId).run();
      return pageId;
    },
    onSuccess: (deletedPageId) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: pageKeys.detail(finalAccountId!, deletedPageId),
      });

      // Update the list
      queryClient.setQueryData(
        pageKeys.list(finalAccountId!, canvasId),
        (oldData: Page[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.filter((page) => page.id !== deletedPageId);
        },
      );
    },
  });
}
